import { useCallback, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { addAllowedCrewMembers, AddStaffNumbersResponse } from "../../../../../store/features/allowedCrewMembers/allowedCrewMembersAPI";
import { useAppDispatch } from '../../../../../store/hooks';
import { loadAllowedCrewMembers } from '../../../../../store/features/allowedCrewMembers/allowedCrewMembersSlice';
import BulkAddDialog, { BulkAddDialogResponseMessage } from '../../../components/BulkAddDialog/BulkAddDialog';

export interface AddCrewDialogProps {
    open: boolean;
    onClose: () => void;
}

const MAX_STAFF_NUMBER_LENGTH = 6;

const AddCrewDialog: FC<AddCrewDialogProps> = ({ open, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const MAX_ITEMS = 20;

    const renderResponseDetails = useCallback((result: AddStaffNumbersResponse) => {
        return (
            <>
                {result.duplicateCount > 0 && result.duplicates && result.duplicates.length > 0 && (
                    <div>
                        <div>
                            <strong>{t('Dialog_AddCrew_DuplicatesLabel')}</strong>
                        </div>
                        <div>
                            {result.duplicates.join(', ')}
                        </div>
                    </div>
                )}
                {result.failureCount > 0 && result.failures && result.failures.length > 0 && (
                    <div>
                        <div>
                            <strong>{t('Dialog_AddCrew_FailuresLabel')}:</strong>
                        </div>
                        <div>
                            {result.failures.map((failure, idx) => (
                                <div key={idx}>
                                    {failure.staffNumber}: {failure.reason}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </>
        );
    }, [t]);

    const getResponseMessage = useCallback((result: AddStaffNumbersResponse): BulkAddDialogResponseMessage | null => {
        if (result.successCount === 0 && result.duplicateCount === 0 && result.failureCount === 0) {
            return null;
        }

        let messageKey = '';

        if (result.successCount > 0 && result.duplicateCount === 0 && result.failureCount === 0) {
            messageKey = 'Dialog_AddCrew_SuccessMessage';
        } else {
            messageKey = 'Dialog_AddCrew_PartialSuccessMessage';
        }

        return {
            type: result.failureCount > 0 ? 'warning' : 'success',
            message: t(messageKey, {
                successCount: result.successCount,
                duplicateCount: result.duplicateCount,
                failedCount: result.failureCount,
            }),
        };
    }, [t]);

    return (
        <BulkAddDialog<AddStaffNumbersResponse>
            open={open}
            title={t('Dialog_AddCrew_Title')}
            fieldLabel={t('Dialog_AddCrew_FieldLabel')}
            fieldHint={t('Dialog_AddCrew_FieldHint')}
            processingLabel={t('Dialog_AddCrew_Processing')}
            saveButtonLabel={t('Button_Common_Save')}
            countBadgeLabel={(count) => t('Dialog_AddCrew_CountBadge', { count })}
            duplicateItemsLabel={(count) => t('Dialog_AddCrew_DuplicatesBar', { count })}
            invalidItemsLabel={(count) => t('Dialog_AddCrew_InvalidBar', { count })}
            maxItems={MAX_ITEMS}
            itemPattern={/^\d{1,6}$/}
            maxItemsErrorMessage={t('Dialog_AddCrew_MaxItemsExceeded', { max: MAX_ITEMS })}
            invalidMessage={`${t('Dialog_AddCrew_InvalidErrMsg')} Staff numbers must contain only digits and be no more than ${MAX_STAFF_NUMBER_LENGTH} digits long.`}
            onClose={onClose}
            submitItems={addAllowedCrewMembers}
            getResponseMessage={getResponseMessage}
            renderResponseDetails={renderResponseDetails}
            onSubmitted={() => {
                dispatch(loadAllowedCrewMembers());
            }}
        />
    );
};

export default AddCrewDialog;